import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { useSupabaseWithSession } from '@/hooks/useSupabase';
import { getWarehouseLineStore } from '@/services/warehouseLineService';
import {
  matchWarehouseLine, warehouseSendActive, warehouseUnresolved,
  type WarehouseValue
} from '../../supabase/functions/_shared/warehouse-line-contract';
import type { ProspectLoan } from '@/components/encompass/prospects/types';

type WarehouseStatusTone = 'idle' | 'saving' | 'success' | 'warning' | 'error';

export function useWarehouseLine(loan: ProspectLoan, enabled = true) {
  const { supabase, user } = useSupabaseWithSession();
  const organizationId = loan.organization_id ?? '';
  const key = `${user?.id}:${organizationId}:${loan.id}`;
  const store = useMemo(() => getWarehouseLineStore(supabase, user?.id ?? '', organizationId),
    [supabase, user?.id, organizationId]);
  const subscribe = useCallback((notify: () => void) => enabled && user?.id
    ? store.subscribe(loan.id, loan.encompass_loan_id, notify) : () => {},
  [store, enabled, user?.id, loan.id, loan.encompass_loan_id]);
  const getSnapshot = useCallback(() => store.get(loan.id, loan.encompass_loan_id), [store, loan.id, loan.encompass_loan_id]);
  const state = useSyncExternalStore(subscribe, getSnapshot);

  useEffect(() => {
    // Only the table RPC supplies a complete value/status snapshot. Sheet loan
    // props are display fallbacks; they cannot replace a confirmed queue state.
    if (enabled && loan.warehouse_revision !== undefined && loan.warehouse_write !== undefined) {
      store.accept({
        loanId: loan.id, encompassLoanId: loan.encompass_loan_id,
        value: loan.warehouse_company_name ?? null, revision: String(loan.warehouse_revision),
        write: loan.warehouse_write, requests: []
      });
    } else if (enabled) {
      store.invalidate(loan.id);
    }
  }, [store, enabled, loan.id, loan.encompass_loan_id, loan.warehouse_company_name, loan.warehouse_revision, loan.warehouse_write, loan.updated_at]);

  const [draft, setDraft] = useState<{ key: string; value: WarehouseValue } | null>(null);
  const source = state.snapshot ? state.snapshot.value : loan.warehouse_company_name ?? null;
  const sourceValue = matchWarehouseLine(source) ?? source ?? '';
  const currentDraft = draft?.key === key ? draft : null;
  const isDirty = !!currentDraft && currentDraft.value !== sourceValue;
  useEffect(() => {
    setDraft(current => current?.key === key && current.value === sourceValue ? null : current);
  }, [key, sourceValue]);
  const busy = state.saving;
  const unresolved = warehouseUnresolved(state.snapshot);
  const sendActive = warehouseSendActive(state.snapshot?.write ?? null);
  const blocked = busy || sendActive || state.attempt?.phase === 'unknown'
    || !!state.snapshot?.write?.outcomeUnknown || state.snapshot?.write?.status === 'syncing';
  const save = useCallback(async (value: WarehouseValue, retry = false) => {
    if (!enabled || !user?.id) {
      return false;
    }
    return store.save(loan.id, loan.encompass_loan_id, value, retry);
  }, [enabled, user?.id, store, loan.id, loan.encompass_loan_id]);
  const saveDraft = async () => {
    if (!currentDraft || !isDirty) {
      return true;
    }
    const accepted = await save(currentDraft.value);
    if (accepted) {
      setDraft(current => current?.key === key && current.value === currentDraft.value ? null : current);
    }
    return accepted;
  };
  const retry = () => {
    const value = state.attempt?.value ?? state.snapshot?.write?.value;
    const normalized = value === '' ? '' : matchWarehouseLine(value);
    return normalized !== null ? save(normalized, true) : Promise.resolve(false);
  };
  let status = '';
  const statusTone: WarehouseStatusTone = busy || state.snapshot?.write?.status === 'syncing' ? 'saving'
    : state.attempt || state.snapshot?.write?.outcomeUnknown || state.snapshot?.write?.status === 'failed' ? 'error'
    : state.readError || state.paused || state.snapshot?.write?.status === 'pending' ? 'warning'
    : state.snapshot?.write?.status === 'synced' ? 'success' : 'idle';
  if (busy) {
    status = 'Saving warehouse…';
  } else if (state.attempt) {
    status = state.attempt.error ?? "Couldn't confirm the warehouse save";
  } else if (state.snapshot?.write?.outcomeUnknown) {
    status = "Couldn't confirm the Encompass save";
  } else if (state.snapshot?.write?.status === 'syncing') {
    status = 'Syncing to Encompass';
  } else if (state.snapshot?.write?.status === 'pending') {
    status = state.snapshot.write.error === 'Loan is locked'
      ? 'Saved. Waiting for Encompass to unlock.' : 'Saved. Pending Encompass sync.';
  } else if (state.snapshot?.write?.status === 'failed') {
    status = state.snapshot.write.error ?? 'Encompass sync failed';
  } else if (state.snapshot?.write?.status === 'synced') {
    status = 'Synced to Encompass';
  }
  return {
    value: state.attempt?.value ?? sourceValue, draftValue: currentDraft?.value ?? state.attempt?.value ?? sourceValue,
    setDraft: (value: WarehouseValue) => setDraft({ key, value }),
    isDirty, saveDraft, save, retry, check: () => store.check(loan.id),
    busy, blocked, status, statusTone, readError: state.readError,
    checking: state.checking, paused: state.paused,
    canRetry: (unresolved || !!state.attempt) && !sendActive && !busy,
    needsCheck: unresolved || !!state.attempt || !!state.readError || state.paused
  };
}

export type WarehouseLineControl = ReturnType<typeof useWarehouseLine>;
